/**
 * PotActionsMenu Molecule
 * Context menu for pot actions in the approval list
 * Pattern: Atomic Design - Molecules
 */

import { useState, useCallback, useId } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon } from '@mui/material';
import {
  MoreHorizontalIcon,
  FastForwardIcon,
  GitMergeIcon,
} from 'lucide-react';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import type { PotStatus } from '../types/pot.types';

export interface PotActionsMenuProps {
  potId: string;
  status: PotStatus;
  isPrimary: boolean;
  onMoveToActivePeriod?: (potId: string) => void;
  onMoveToPrimary?: (potId: string) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Context menu for pot row actions
 * Shows available actions based on pot status and user permissions
 */
export function PotActionsMenu({
  potId,
  status,
  isPrimary,
  onMoveToActivePeriod,
  onMoveToPrimary,
  disabled,
  'data-testid': dataTestId,
}: PotActionsMenuProps) {
  const menuId = useId();
  const buttonId = `pot-actions-btn-${menuId}`;
  const menuElId = `pot-actions-menu-${menuId}`;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback((event?: React.MouseEvent) => {
    event?.stopPropagation();
    setAnchorEl(null);
  }, []);

  const handleMoveToActivePeriod = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      handleClose();
      onMoveToActivePeriod?.(potId);
    },
    [potId, onMoveToActivePeriod, handleClose]
  );

  const handleMoveToPrimary = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      handleClose();
      onMoveToPrimary?.(potId);
    },
    [potId, onMoveToPrimary, handleClose]
  );

  // Determine which actions are available based on status
  const canMoveToActivePeriod =
    status === 'MissedSubmitDeadline' ||
    status === 'MissedVerificationDeadline';
  const canMoveToPrimary =
    !isPrimary &&
    status !== 'MissedSubmitDeadline' &&
    status !== 'MissedVerificationDeadline';
  const hasAnyAction = canMoveToActivePeriod || canMoveToPrimary;

  return (
    <>
      <IconButton
        id={buttonId}
        size="small"
        onClick={handleClick}
        disabled={disabled || !hasAnyAction}
        aria-label="More actions"
        aria-controls={open ? menuElId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        data-testid={dataTestId}
        sx={{ flexShrink: 0, transition: 'all 0.2s ease' }}
      >
        <MoreHorizontalIcon size={18} />
      </IconButton>

      <Menu
        id={menuElId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        onClick={e => e.stopPropagation()}
        MenuListProps={{
          'aria-labelledby': buttonId,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {canMoveToActivePeriod && (
          <PermissionGate permission={Permissions.Pots.Move}>
            <MenuItem
              onClick={handleMoveToActivePeriod}
              data-testid="move-to-active-period-menu-item"
            >
              <ListItemIcon>
                <FastForwardIcon size={18} />
              </ListItemIcon>
              Move to Active Period
            </MenuItem>
          </PermissionGate>
        )}
        {canMoveToPrimary && (
          <PermissionGate permission={Permissions.Pots.Move}>
            <MenuItem
              onClick={handleMoveToPrimary}
              data-testid="move-to-primary-menu-item"
            >
              <ListItemIcon>
                <GitMergeIcon size={18} />
              </ListItemIcon>
              Move to Primary Pot
            </MenuItem>
          </PermissionGate>
        )}
      </Menu>
    </>
  );
}
